import {
  Eye,
  FileSearch,
  FileText,
  Zap,
  Database,
  Code as CodeIcon,
  Network,
  Map as MapIcon,
  ShieldCheck,
  Files,
  Upload,
  Rocket,
  Settings as SettingsIcon,
  type LucideIcon,
} from "lucide-react";

export const RAIL_GROUPS = [
  { id: "workflow", label: "Create" },
  { id: "review", label: "Review" },
  { id: "ship", label: "Release" },
  { id: "advanced", label: "Details" },
  { id: "configure", label: "Configure" },
] as const;
export type RailGroup = (typeof RAIL_GROUPS)[number]["id"];

export type RailItemId =
  | "preview"
  | "source"
  | "editor"
  | "behavior"
  | "data"
  | "library"
  | "code"
  | "map"
  | "evidence"
  | "report"
  | "document"
  | "publish"
  | "deploy"
  | "settings";

export interface RailItem {
  id: RailItemId;
  label: string;
  group: RailGroup;
  icon: LucideIcon;
  step?: number;
}

/**
 * User-intent-centered workflow ordering.
 *
 * Primary rail flows from what a Studio user is trying to finish:
 *   App -> Pages -> Logic -> Data -> Review -> Report -> Release.
 * Source decisions, source maps, and code remain explanation/debug layers
 * rather than the first thing a creator must understand.
 */
export const RAIL_ITEMS: readonly RailItem[] = [
  { id: "preview", label: "App", group: "workflow", icon: Eye, step: 1 },
  {
    id: "source",
    label: "Design sources",
    group: "workflow",
    icon: FileSearch,
    step: 2,
  },
  { id: 'editor', label: 'Pages', group: 'workflow', icon: FileText, step: 3 },
  { id: 'behavior', label: 'Behavior', group: 'workflow', icon: Zap, step: 4 },
  { id: 'data', label: 'Data', group: 'workflow', icon: Database, step: 5 },
  { id: 'evidence', label: 'Checks', group: 'review', icon: ShieldCheck },
  { id: 'report', label: 'Status', group: 'review', icon: Files },
  { id: 'document', label: 'Guide', group: 'review', icon: FileText },
  { id: 'publish', label: 'Publish', group: 'ship', icon: Upload },
  { id: 'deploy', label: 'Launch', group: 'ship', icon: Rocket },
  { id: 'library', label: 'Details', group: 'advanced', icon: Network },
  { id: 'map', label: 'Map', group: 'advanced', icon: MapIcon },
  { id: 'code', label: 'Code', group: 'advanced', icon: CodeIcon },
  { id: 'settings', label: 'Settings', group: 'configure', icon: SettingsIcon },
] as const

export const CREATOR_RAIL_ITEMS = new Set<RailItemId>([
  "preview",
  "source",
  "editor",
  "behavior",
  "data",
  "evidence",
  "report",
  "document",
  "publish",
  "deploy",
  "settings",
]);

// ── Phase A.1 — Build mode ──

export const STUDIO_MODES = ["build", "edit"] as const;
export type StudioMode = (typeof STUDIO_MODES)[number];
